import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Tooltip, { TooltipProps, tooltipClasses } from '@mui/material/Tooltip';

// import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
// import moment from 'moment';
import React from 'react';

const dev = process.env.NODE_ENV !== 'production';
const URL_WIB = dev ? process.env.NEXT_PUBLIC_URL_WIB : process.env.NEXT_PUBLIC_PRODUCTION_URL_WIB;

type Job = {
  _id: string;
  title: string;
  jobUrl: string;
  location: string;
  datePosted: Date;
  validThrough: Date;
  companyName: string;
  companyLogo: string;
  companyId: string;
  isJobRaw: boolean;
  scriptId: string;
  categorySlug: string;
  categoryId: string;
  state: string;
  companySize: string;
  employmentType: string;
  estimatedSalary: { minValue: string; maxValue: string };
  isHighlighted: boolean;
  isPublished: boolean;
  companySlug: string;
  companyWebsite: string;
};

type Props = {
  job: Job;
  onCompanyNameClick?: () => void;
  companyInfo?: any;
  page: string;
  kind?: string;
  /** Compact vertical card when featured jobs are shown in a grid */
  featuredVariant?: 'default' | 'grid';
};

const BootstrapTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} arrow classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.arrow}`]: {
    color: theme.palette.common.black,
  },
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.black,
    color: theme.palette.common.white,
    fontSize: 12,
  },
}));

class JobDetail extends React.Component<Props> {
  public render() {
    const { job, companyInfo, kind, page, featuredVariant } = this.props;
    const isFeaturedGrid = kind === 'featured' && featuredVariant === 'grid';

    if (!job) {
      return null;
    }

    const getMargin = () => {
      if (kind === 'featured') {
        return isFeaturedGrid ? 0 : '10px 0px';
      } else if (page === 'index') {
        return { xs: '15px 1%', md: '10px 20%' };
      } else {
        return { xs: '15px 1%', md: '10px 20px' };
      }
    };

    return (
      <Paper
        sx={{
          margin: getMargin(),
          padding: '12px',
          backgroundColor: job && job.isHighlighted ? '#ffd50033' : null,
          height: isFeaturedGrid ? '100%' : undefined,
          display: isFeaturedGrid ? 'flex' : undefined,
          flexDirection: isFeaturedGrid ? 'column' : undefined,
          boxSizing: 'border-box',
        }}
        elevation={3}
      >
        {this.renderPostDetail(job, companyInfo, page, isFeaturedGrid)}
      </Paper>
    );
  }

  public renderPostDetail(job: Job, companyInfo, page, isFeaturedGrid = false) {
    if (!job && companyInfo.scriptId === '61e4aeea4555cf5673c67ed3') {
      const randomString8 =
        Math.random().toString(36).substring(2, 6) + Math.random().toString(36).substring(2, 6);

      return (
        <React.Fragment>
          <Box
            id={`job-${randomString8}`}
            sx={{
              display: { xs: 'block', md: 'flex' },
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            {companyInfo.logo && (
              <Box
                sx={{
                  margin: '0px auto 0px 5%',
                  width: '22%',
                  textAlign: 'center',
                  display: { xs: 'none', md: 'block' },
                }}
              >
                <Box
                  component="img"
                  sx={{
                    maxWidth: '100%',
                    maxHeight: '75px',
                    height: 'auto',
                    width: 'auto',
                    verticalAlign: 'middle',
                    imageRendering: '-webkit-optimize-contrast',
                  }}
                  src={companyInfo.logo}
                  alt={companyInfo.name}
                />
              </Box>
            )}

            <Box
              sx={{
                textAlign: 'center',
                width: { xs: '100%', md: '58%' },
              }}
            >
              <span style={{ fontWeight: 600 }}>All jobs</span>
              {page === 'index' ? (
                <div style={{ marginTop: '8px' }}>
                  <a
                    href={
                      companyInfo.jobBoardUrl || `${URL_WIB}/companies/${companyInfo.companySlug}`
                    }
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                  >
                    {companyInfo.name}
                  </a>
                  <span style={{ margin: '0px 8px' }}>|</span>
                  {companyInfo.location} <p style={{ marginTop: '-10px' }} />
                  Size: {companyInfo.employeeRange}
                </div>
              ) : null}
            </Box>

            <Box sx={{ width: { xs: '100%', md: '15%' }, textAlign: 'center' }}>
              <Button
                href={companyInfo.jobBoardUrl}
                target="_blank"
                rel="noopener noreferrer"
                component="a"
                variant="contained"
                color="primary"
                sx={{
                  marginTop: { xs: '10px', md: '0px' },
                }}
              >
                See jobs
              </Button>
            </Box>
          </Box>
        </React.Fragment>
      );
    }

    const date1 = new Date();
    const date2 = new Date(job.datePosted);
    const diffTime = Math.abs(date2.valueOf() - date1.valueOf());
    const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));

    let daysAgo = '';

    if (diffDays > 100) {
      daysAgo = '100+ days ago';
    } else if (diffDays === 0) {
      daysAgo = 'today';
    } else if (diffDays === 1) {
      daysAgo = `${diffDays} day ago`;
    } else if (Number.isNaN(diffDays)) {
      daysAgo = 'Posting date unknown ';
    } else {
      daysAgo = `${diffDays} days ago`;
    }

    return (
      <React.Fragment>
        {job && !job.isJobRaw ? (
          <Box
            id={`job-${job._id}`}
            sx={{
              display: 'flex',
              flexDirection: isFeaturedGrid ? 'column' : { xs: 'column', md: 'row' },
              alignItems: 'center',
              justifyContent: isFeaturedGrid ? 'flex-start' : 'space-between',
              flex: isFeaturedGrid ? 1 : undefined,
              gap: isFeaturedGrid ? 1.5 : 0,
              width: '100%',
            }}
          >
            {job.companyLogo && (
              <Box
                sx={{
                  margin: isFeaturedGrid ? 0 : '0px auto 0px 5%',
                  width: isFeaturedGrid ? '100%' : '22%',
                  textAlign: 'center',
                  display: isFeaturedGrid
                    ? 'flex'
                    : { xs: 'none', md: page === 'index' ? 'block' : 'none' },
                  minHeight: isFeaturedGrid ? 56 : undefined,
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Box
                  component="img"
                  sx={{
                    maxWidth: '100%',
                    maxHeight: isFeaturedGrid ? '56px' : '70px',
                    height: 'auto',
                    width: 'auto',
                    verticalAlign: 'middle',
                    imageRendering: '-webkit-optimize-contrast',
                  }}
                  src={job.companyLogo}
                  alt={job.companyName}
                />
              </Box>
            )}

            <Box
              sx={{
                textAlign: 'center',
                width: isFeaturedGrid
                  ? '100%'
                  : { xs: '100%', md: page === 'index' ? '58%' : '80%' },
                flex: isFeaturedGrid ? 1 : undefined,
              }}
            >
              <BootstrapTooltip title={job.title} placement="top">
                <Box
                  component="span"
                  sx={{
                    fontWeight: 600,
                    display: isFeaturedGrid ? '-webkit-box' : undefined,
                    WebkitLineClamp: isFeaturedGrid ? 3 : undefined,
                    WebkitBoxOrient: isFeaturedGrid ? 'vertical' : undefined,
                    overflow: isFeaturedGrid ? 'hidden' : undefined,
                    lineHeight: isFeaturedGrid ? 1.35 : undefined,
                  }}
                >
                  {!isFeaturedGrid ? (
                    <>
                      <Box
                        component="span"
                        sx={{
                          display: { xs: 'none', md: 'inline' },
                        }}
                      >
                        {job.title.length < 50 ? job.title : job.title.substring(0, 50) + '...'}
                      </Box>
                      <Box
                        component="span"
                        sx={{
                          display: { xs: 'inline', md: 'none' },
                        }}
                      >
                        {job.title.length < 30 ? job.title : job.title.substring(0, 30) + '...'}
                      </Box>
                    </>
                  ) : (
                    job.title
                  )}
                </Box>
              </BootstrapTooltip>
              <div
                style={{
                  marginTop: '8px',
                  fontSize: isFeaturedGrid ? 14 : undefined,
                  lineHeight: 1.4,
                }}
              >
                {page === 'index' ? (
                  <>
                    <a
                      href={
                        companyInfo?.jobBoardUrl ||
                        job.companyWebsite ||
                        `${URL_WIB}/companies/${job.companySlug}`
                      }
                      target="_blank"
                      rel="nofollow noopener noreferrer"
                    >
                      {job.companyName}
                    </a>
                    <span style={{ margin: '0 4px', opacity: isFeaturedGrid ? 0.5 : undefined }}>
                      |
                    </span>
                    {job.location}
                    {!isFeaturedGrid ? <p style={{ marginTop: '-10px' }} /> : null}
                    {job.companySize && !isFeaturedGrid ? (
                      <>
                        Size: {job.companySize}
                        <span
                          style={{
                            margin: '0px 8px',
                            display: 'inline-flex',
                          }}
                        >
                          |
                        </span>
                        {daysAgo}
                      </>
                    ) : null}
                  </>
                ) : (
                  <>
                    {' '}
                    {job.location}
                    <span
                      style={{
                        margin: '0px 8px',
                        display: 'inline-flex',
                      }}
                    >
                      |
                    </span>
                    {daysAgo}
                  </>
                )}
              </div>
            </Box>

            <Box
              sx={{
                width: isFeaturedGrid ? '100%' : { xs: '100%', md: '15%' },
                textAlign: 'center',
                mt: isFeaturedGrid ? 'auto' : 0,
              }}
            >
              <BootstrapTooltip title={job.jobUrl}>
                <Button
                  href={job.jobUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  component="a"
                  variant="contained"
                  color="primary"
                  size={isFeaturedGrid ? 'small' : 'medium'}
                  fullWidth={isFeaturedGrid}
                  sx={{
                    marginTop: isFeaturedGrid ? 0 : { xs: '10px', md: '0px' },
                  }}
                >
                  Apply
                </Button>
              </BootstrapTooltip>
            </Box>
          </Box>
        ) : null}
      </React.Fragment>
    );
  }

  // private onCompanyNameClick = () => {
  //   this.props.onCompanyNameClick();
  // };
}

export default JobDetail;
